/**
 * Mit dieser Klasse können weitere Ressourcen nachgeladen werden.
 * Dabei wird die vom Parent-Frame verwendete Domain verwendet.
 */
export class AsyncLoader {
	/**
	 * Erstelle einige Standardvariablen für den AsyncLoader.
	 */
	constructor() {
		this.domainSources = [];
		this.domains       = [];
		
		this.loaded        = [];
		this.loadCallbacks = {};
	}

	/**
	 * Diese Funktion lädt eine weitere JS-Datei.
	 *
	 * @param {String}   relativeUrl Die relative URL von der JS-Datei.
	 * @param {Function} fnSuccess   Diese Callback-Function wird ausgeführt,
	 * wenn ein Versucht erfolgreich durchgeführt wurde.
	 * @param {Function} fnError     Diese Callback-Funktion wird ausgeführt,
	 * wenn alle Versuche fehlgeschlagen sind.
	 */
	loadJsFile(relativeUrl, fnSuccess, fnError) {
		function onLoad(url, onError, onSuccess) {
			var file = document.createElement('script');
			file.setAttribute('type', 'text/javascript');
			file.setAttribute('src', url);
			file.setAttribute('crossorigin', 'use-credentials');
			file.onerror = onError;
			file.onload  = onSuccess;
			document.getElementsByTagName('head')[0].appendChild(file);
		}
		this.load(relativeUrl, onLoad, fnSuccess, fnError);
	}

	/**
	 * Diese Funktion lädt eine weitere CSS-Datei.
	 *
	 * @param {String}   relativeUrl Die relative URL von der CSS-Datei.
	 * @param {Function} fnSuccess   Diese Callback-Function wird ausgeführt,
	 * wenn ein Versucht erfolgreich durchgeführt wurde.
	 * @param {Function} fnError     Diese Callback-Funktion wird ausgeführt,
	 * wenn alle Versuche fehlgeschlagen sind.
	 */
	loadCssFile(relativeUrl, fnSuccess, fnError) {
		function onLoad(url, onError, onSuccess) {
			var file = document.createElement('link');
			file.setAttribute('rel', 'stylesheet');
			file.setAttribute('type', 'text/css');
			file.setAttribute('href', url);
			file.setAttribute('crossorigin', 'use-credentials');
			file.onerror = onError;
			file.onload  = onSuccess;
			document.getElementsByTagName('head')[0].appendChild(file);
		}
		this.load(relativeUrl, onLoad, fnSuccess, fnError);
	}

	/**
	 * Diese Funktion ermögicht eine abhängige Ausführung von Skripten. Erst wenn
	 * bestimmte Resourcen geladen wurden, wird die Callback-Funktion ausgeführt.
	 *
	 * @param {String}   relativeUrl Die Callback-Funkton ist von dieser Ressource
	 * abhängig.
	 * @param {Function} fnCallback  Diese Callback-Funktion wird ausgeführt,
	 * sobald die Ressource geladen wurde.
	 */
	executeAfter(relativeUrl, fnCallback) {
		if (this.loaded.includes(relativeUrl)) {
			fnCallback();
			return;
		}
		if(!Object.prototype.hasOwnProperty.call(this.loadCallbacks, relativeUrl)) {
			this.loadCallbacks[relativeUrl] = [];
		}
		this.loadCallbacks[relativeUrl].push(fnCallback);
	}

	/**
	 * Diese Funktion probiert alle eingetragenen Kombinationen aus der Domain
	 * und dem relativen Pfad. Diese wird dem Callback übergeben. Wenn eine
	 * Kombination erfolgreich war, kann jeder weiterer Aufruf ignoriert werden.
	 * Wenn die Seite nicht gefunden wurde, oder dabei ein Fehler aufgetretten ist,
	 * dann kann mit dem Callback "onNext" die nächste Kombination probiert werden.
	 *
	 * @param {String}   relativeUrl Die relative URL zur Ressource.
	 * @param {Function} fnLoad      Diese Callback-Funktion wird mit jedem neuen
	 * Versuch ausgeführt.
	 * @param {Function} fnSuccess   Diese Callback-Funktion wird ausgeführt,
	 * sobald ein Versucht erfolgreich durchgeführt wurde.
	 * @param {Function} fnError     Diese Callback-Funktion wird ausgeführt,
	 * sobald alle Versuche fehlgeschlagen sind.
	 */
	load(relativeUrl, fnLoad, fnSuccess, fnError) {
		var self = this;
	
		/**
		 * Diese Funktion versucht die nächste Domain.
		 *
		 * @param {Number} index Der Index von der Quelle.
		 */
		function tryNext(index) {
			if (!self.domains[index]) {
				prepareNext(index + 1);
				return;
			}
			fnLoad(
				self.domains[index] + '/' + relativeUrl,
				function onError() {
					prepareNext(index + 1);
				},
				function onSuccess() {
					if (fnSuccess) {
						fnSuccess();
					}
					if (Object.prototype.hasOwnProperty.call(self.loadCallbacks, relativeUrl)) {
						self.loadCallbacks[relativeUrl].forEach(function(fnCallback) {
							fnCallback();
						});
						self.loadCallbacks[relativeUrl] = [];
					}
					self.loaded.push(relativeUrl);
				}
			);
		}
	
		/**
		 * Diese Funktion bereitet die nächste Domain vor.
		 *
		 * @param {Number} index Der Index von der Quelle.
		 */
		function prepareNext(index) {
			if (index > self.domainSources.length) {
				if (fnError) {
					fnError();
				}
				return;
			}
			if (!self.domains[index]) {
				self.domainSources[index].call(self, function(domain) {
					if (domain) {
						self.domains[index] = domain;
					}
					tryNext(index);
				});
				return;
			}
			tryNext(index);
		}
		prepareNext(0);
	}

	/**
	 * Diese Funktion fügt eine weitere Quelle für die Domains hinzu.
	 *
	 * @param {Callback} fnLoad Eine Funktiom die eine neue Domain zurückgibt.
	 */
	pushSource(fnLoad) {
		this.domainSources.push(fnLoad);
	}

	/**
	 * Diese Funktion gibt aus dem Query-String eines URLs ein besimmten Wert zurück.
	 *
	 * @param {String} name Der Key von der Query-Variable.
	 * @param {String} url  Die URL mit dem Query-String.
	 * @return {String|null}
	 */
	static getQueryParameter(name, url) {
		url = url || document.URL;
		if (url.length > 0 && name.length > 0) {
			name = name + '=';
			var begin = url.indexOf(name);
			if (begin !== -1) {
				begin += name.length;
				var end = url.indexOf('&', begin);
				if (end === -1) {
					end = url.length;
				}
				return decodeURIComponent(url.substring(begin, end));
			}
		}
		return null;
	}
}
